/**
 * MFX OS chain — controlled record types (Release 1 foundation).
 *
 * Blueprint: claude/MFX-OS-Master-Blueprint.md (project MFX ENGINES).
 * Conventions follow the live mfx-2026 ecosystem: ISO-string timestamps
 * (matches @mfx/contracts), top-level collections, additive compatibility
 * with existing `salesOrders` / `jobTickets` docs the OS + portal render.
 */

// ── actors + shared metadata ─────────────────────────────────────────────
export interface Actor { uid: string; email: string; name: string }

export interface Meta {
  displayId: string;
  status: string;
  createdAt: string;  createdBy: string;   // ISO / email
  updatedAt: string;  updatedBy: string;
  version: number;
  isArchived: boolean;
  source: 'mfx-os';
  clientId?: string;
  clientName?: string;
  tags?: string[];
}

// ── Customer PO (Stage 1-2) ──────────────────────────────────────────────
export const PO_STATUSES = [
  'RECEIVED', 'MATCHING', 'REVIEW_REQUIRED', 'EXCEPTION',
  'WAITING_ON_CLIENT', 'APPROVED', 'SUPERSEDED', 'REJECTED',
] as const;
export type POStatus = (typeof PO_STATUSES)[number];

export type POChannel = 'portal' | 'email' | 'manual' | 'salesperson' | 'api';

export interface POLine {
  id: string;
  customerPart: string;
  description: string;
  qty: number;
  unitPrice: number;
  total: number;
  /** quote tier/sku this maps to ('' = unmapped) */
  mappedTo: string;
  mapStatus: 'unmapped' | 'mapped' | 'mismatch';
  quoteUnitPrice?: number;
}

export interface POException {
  reason: string;
  category: 'pricing' | 'quantity' | 'date' | 'shipto' | 'terms' | 'other';
  impact: string;
  riskNote: string;
  approvedBy: string;
  approvedAt: string;
}

export interface CustomerPO extends Meta {
  status: POStatus;
  poNumber: string;
  clientEmail?: string;
  channel: POChannel;
  receivedAt: string;
  reviewer?: string;
  quoteId?: string;
  quoteNum?: string;
  total?: number;
  requestedDate?: string;
  shipTo?: string;
  billTo?: string;
  paymentTerms?: string;
  notes?: string;
  fileUrl?: string;
  rawText?: string;
  lines: POLine[];
  duplicateOfId?: string;
  exception?: POException;
  clarification?: { question: string; askedAt: string; askedBy: string; answer?: string; resolvedAt?: string };
  comparison?: CompareResult;
  salesOrderId?: string;
  salesOrderNum?: string;
  /** provenance when imported from a website-portal PO submission */
  portalQuoteDocId?: string;
}

// ── PO ↔ quote comparison (Stage 2) ──────────────────────────────────────
export type Verdict = 'match' | 'mismatch' | 'missing' | 'info';

export interface CompareRow { field: string; label: string; quote: string; po: string; verdict: Verdict }

export interface CompareResult {
  ranAt: string;
  quoteId: string;
  quoteNum: string;
  rows: CompareRow[];
  mismatches: number;
  unmappedLines: number;
  blockers: string[];   // hard rules that stop approval
}

// ── Sales Order (Stage 3-4) — additive superset of live salesOrders docs ─
export interface StatusTracks {
  commercial:  'DRAFT' | 'APPROVED' | 'EXCEPTION' | 'CLOSED';
  production:  'NOT_READY' | 'READY' | 'IN_PRODUCTION' | 'COMPLETE';
  fulfillment: 'NOT_SHIPPED' | 'PARTIAL' | 'SHIPPED' | 'DELIVERED';
  invoice:     'NOT_INVOICED' | 'DRAFT' | 'SENT' | 'SYNCED';
  payment:     'UNPAID' | 'PARTIAL' | 'PAID';
}

export const DEFAULT_TRACKS: StatusTracks = {
  commercial: 'APPROVED', production: 'NOT_READY',
  fulfillment: 'NOT_SHIPPED', invoice: 'NOT_INVOICED', payment: 'UNPAID',
};

export interface SalesOrderOS extends Meta {
  /** legacy-compat fields the OS app + portal already render */
  soNum: string;                  // = displayId
  customerName: string;
  customerId?: string | null;
  email?: string;
  poNumber: string;
  total: number;
  skus: string[];
  skuCount: number;
  requestedDate?: string;
  promisedDate?: string;
  poRequiredDate?: string;        // legacy alias of requestedDate
  estimatedShipDate?: string;     // legacy alias of promisedDate
  /** OS chain */
  customerPOId: string;
  quoteId?: string;
  quoteNum?: string;
  quoteLock?: { quoteId: string; quoteNum: string; lockedAt: string; totalAmount?: number };
  poLock?: { customerPOId: string; poNumber: string; lockedAt: string; poVersion: number };
  tracks: StatusTracks;
  lineIds: string[];
  jobTicketIds: string[];
  ack?: { preparedAt: string; body: string; sentAt?: string; sentBy?: string };
}

export type LineResolution = 'unresolved' | 'existing' | 'changed' | 'new';

export interface SalesOrderLine extends Meta {
  salesOrderId: string;
  soNum: string;
  lineNo: number;
  customerPart: string;
  internalPart?: string;
  description: string;
  qty: number;
  unitPrice: number;            // quote pricing preserved
  poUnitPrice?: number;         // what the PO said (kept for evidence)
  total: number;
  requestedDate?: string;
  promisedDate?: string;
  resolution: LineResolution;
  itemId?: string;
  blueprintRevisionId?: string;
  blueprintRevCode?: string;
  productionJobId?: string;
  changeNote?: string;          // when resolution = 'changed'
}

// ── Items + blueprint revisions (Stage 5, Release 3) ─────────────────────
export interface Item extends Meta {
  internalPart: string;         // = displayId
  customerPart?: string;
  name: string;
  family?: string;
  currentRevisionId?: string;
  currentRevCode?: string;
  lastProducedAt?: string;
  lastPrice?: number;
  lastSalesOrderId?: string;
  knownIssues?: string;
}

export interface BlueprintSpec {
  dimensions?: string; substrate?: string; gauges?: string;
  inkSystem?: string; colors?: string; adhesive?: string; coatings?: string;
  tooling?: string; processRoute?: string; tolerances?: string;
  packaging?: string; inspectionPlan?: string; artworkRef?: string;
}

export interface BlueprintRevision extends Meta {
  status: 'DRAFT' | 'APPROVED' | 'SUPERSEDED';
  itemId: string;
  internalPart: string;
  revCode: string;              // A, B, C…
  reason: string;
  spec: BlueprintSpec;
  approvedBy?: string;
  approvedAt?: string;
  effectiveAt?: string;
  supersedesId?: string;
}

// ── Ticket + production jobs + gates (Stages 6-7, Release 3-4) ───────────
export const GATE_DEFS = [
  { code: 'commercial', label: 'Commercial',   ownerRole: 'CSR' },
  { code: 'prepress',   label: 'Prepress',     ownerRole: 'Prepress' },
  { code: 'logistics',  label: 'Logistics',    ownerRole: 'Logistics' },
  { code: 'quality',    label: 'Quality / SQF', ownerRole: 'Quality' },
  { code: 'accounting', label: 'Accounting',   ownerRole: 'Accounting' },
  { code: 'production', label: 'Production',   ownerRole: 'Scheduler' },
] as const;
export type GateCode = (typeof GATE_DEFS)[number]['code'];

export type GateStatus = 'not_started' | 'in_review' | 'waiting' | 'ready' | 'blocked' | 'overridden' | 'not_applicable';

export interface Gate {
  code: GateCode;
  label: string;
  applicability: 'required' | 'advisory' | 'not_applicable' | 'alternative_control';
  status: GateStatus;
  ownerRole: string;
  ownerEmail?: string;
  dueAt?: string;
  evidence?: string;
  completedBy?: string;
  completedAt?: string;
  blockerReason?: string;
  override?: { reason: string; riskNote: string; approvedBy: string; approvedAt: string };
}

export interface JobTicketOS extends Meta {
  status: string;               // legacy-compat ('open' | 'released' | …)
  salesOrderId: string;
  soNum: string;
  customerName: string;
  dueDate?: string;
  productionJobIds: string[];
  readinessPct: number;
  blockedCount: number;
}

export type JobReadiness = 'blocked' | 'partial' | 'ready';

export interface ProductionJob extends Meta {
  status: 'OPEN' | 'READY' | 'SCHEDULED' | 'IN_PRODUCTION' | 'COMPLETE' | 'CANCELLED';
  jobTicketId: string;
  ticketDisplayId: string;
  salesOrderId: string;
  soNum: string;
  salesOrderLineId: string;
  customerName: string;
  itemId?: string;
  internalPart?: string;
  customerPart?: string;
  description: string;
  qty: number;
  blueprintRevisionId?: string;
  blueprintRevCode?: string;
  gates: Gate[];
  readiness: JobReadiness;
  runIds: string[];
}

// ── Materials scaffold (Stage 10, Release 4) ─────────────────────────────
export const INVENTORY_STATES = ['AVAILABLE','ALLOCATED','QUARANTINED','ON_HOLD','RELEASED','REJECTED','DEPLETED'] as const;

export interface MaterialRequirement extends Meta {
  status: 'OPEN' | 'ALLOCATED' | 'SHORT' | 'CLOSED';
  productionJobId: string;
  jobTicketId: string;
  materialName: string;
  materialId?: string;
  requiredQty: number;
  unit: string;
  wastePct: number;
  availableQty: number;
  allocatedQty: number;
  shortQty: number;
  requiredBy?: string;
  lotControlled: boolean;
}

// ── Events + outbox (Release 1) ──────────────────────────────────────────
export interface OsEvent {
  entityType: 'customerPO' | 'salesOrder' | 'salesOrderLine' | 'item' | 'blueprintRevision' | 'jobTicket' | 'productionJob' | 'materialRequirement';
  entityId: string;
  displayId: string;
  action: string;
  from?: string;
  to?: string;
  reason?: string;
  evidence?: string;
  related?: { type: string; id: string; displayId?: string }[];
  actor: Actor;
  at: string;
}

export interface OutboxEvent {
  type: string;
  payload: Record<string, unknown>;
  status: 'pending' | 'processed' | 'failed';
  createdAt: string;
  createdBy: string;
  processedAt?: string;
  error?: string;
}

// ── status → color language (blueprint 2.4) ──────────────────────────────
export function statusColor(s: string): string {
  const t = String(s || '').toLowerCase();
  if (/(ready|approved|released|complete|paid|match|shipped|delivered|synced)/.test(t)) return 'var(--gn)';
  if (/(waiting|review|due|clarif|partial|draft_review|in_review|matching)/.test(t)) return 'var(--yl)';
  if (/(blocked|failed|hold|overdue|reject|exception|short|mismatch|unpaid)/.test(t)) return 'var(--rd)';
  if (/(active|scheduled|running|received|in_production|open)/.test(t)) return 'var(--ac)';
  return 'var(--tx3)';
}
